/** Input formats accepted by the UI (Firebase Storage has no MIME restrictions). */
export const SUPPORTED_LINK_IMAGE_FORMATS = ['image/jpeg', 'image/png'];
export const SUPPORTED_AVATAR_IMAGE_FORMATS = ['image/jpeg', 'image/png', 'image/webp'];

/** 80px gallery preview × 2 for retina. */
export const LINK_IMAGE_MAX_DIMENSION = 160;

/** 72px account settings avatar × 2 for retina. */
export const AVATAR_MAX_DIMENSION = 144;

const OUTPUT_WEBP_QUALITY = 0.85;
const OUTPUT_JPEG_QUALITY = 0.85;

export const isSupportedLinkImageFormat = (file) => (
	Boolean(file?.type) && SUPPORTED_LINK_IMAGE_FORMATS.includes(file.type)
);

export const isSupportedAvatarImageFormat = (file) => (
	Boolean(file?.type) && SUPPORTED_AVATAR_IMAGE_FORMATS.includes(file.type)
);

export const calculateFitDimensions = (width, height, maxDimension) => {
	const safeWidth = Math.max(1, width);
	const safeHeight = Math.max(1, height);
	const ratio = Math.min(1, maxDimension / Math.max(safeWidth, safeHeight));
	return {
		width: Math.max(1, Math.round(safeWidth * ratio)),
		height: Math.max(1, Math.round(safeHeight * ratio)),
	};
};

const buildOptimizedFileName = (originalName, mimeType) => {
	const base = String(originalName ?? 'image').replace(/\.[^.]+$/i, '') || 'image';
	const ext = mimeType === 'image/webp' ? 'webp' : 'jpg';
	return `${base}.${ext}`;
};

const blobToFile = (blob, originalFile, mimeType) => new File(
	[blob],
	buildOptimizedFileName(originalFile?.name, mimeType),
	{type: mimeType, lastModified: Date.now()}
);

const encodeCanvas = (canvas, sourceFile) => new Promise((resolve) => {
	const handleBlob = (mimeType) => (blob) => {
		if (blob) {
			resolve(blobToFile(blob, sourceFile, mimeType));
			return;
		}
		if (mimeType === 'image/webp') {
			canvas.toBlob(handleBlob('image/jpeg'), 'image/jpeg', OUTPUT_JPEG_QUALITY);
			return;
		}
		resolve(null);
	};
	canvas.toBlob(handleBlob('image/webp'), 'image/webp', OUTPUT_WEBP_QUALITY);
});

const loadImageFromFile = (file) => new Promise((resolve, reject) => {
	const objectUrl = URL.createObjectURL(file);
	const img = new Image();
	img.onload = () => {
		URL.revokeObjectURL(objectUrl);
		resolve(img);
	};
	img.onerror = () => {
		URL.revokeObjectURL(objectUrl);
		reject(new Error('Could not read image file.'));
	};
	img.src = objectUrl;
});

const resizeImageFile = async (file, maxDimension) => {
	if (!(file instanceof Blob)) {
		return file;
	}

	try {
		const img = await loadImageFromFile(file);
		const {width, height} = calculateFitDimensions(img.width, img.height, maxDimension);
		const canvas = document.createElement('canvas');
		canvas.width = width;
		canvas.height = height;
		const context = canvas.getContext('2d');
		if (!context) {
			return file;
		}
		context.drawImage(img, 0, 0, width, height);
		const optimized = await encodeCanvas(canvas, file);
		return optimized ?? file;
	} catch {
		return file;
	}
};

export const optimizeLinkImage = (file) => resizeImageFile(file, LINK_IMAGE_MAX_DIMENSION);

export const optimizeAvatarImage = (file) => resizeImageFile(file, AVATAR_MAX_DIMENSION);
